/**
 * OnbStep — sub-componentes da tela de onboarding (F3).
 *
 * Prefixo Onb* obrigatorio por isolamento de namespace (ver CLAUDE.md regras duras).
 * Importado exclusivamente por OnboardingPage.tsx.
 *
 * Componentes exportados:
 *   - OnbBrandHeader    — logo CROSS + nome no topo
 *   - OnbProgressTrack  — barra de progresso com labels de passo
 *   - OnbPanelHead      — eyebrow + titulo + subtitulo
 *   - OnbBtnRow         — linha de botoes (voltar / continuar)
 *   - OnbSourceCard     — card de fonte individual (li)
 *   - OnbTimelineItem   — item da timeline de sucesso (li)
 */

import type { ReactNode } from 'react';
import { t, type TranslationKey } from '../lib/i18n';

// ------------------------------------------------------------------ tipos

export type OnbSourceKey = 'instagram' | 'metaAds' | 'googleDrive';

export type OnbConnectionKind = 'idle' | 'connecting' | 'pending' | 'connected' | 'error';

// ------------------------------------------------------------------ OnbBrandHeader

/**
 * Logo SVG CROSS (cata-vento tricolor) + nome "CROSS" centralizados.
 * Identico ao prototipo onboarding.html — brand-logo + brand-name.
 */
export function OnbBrandHeader(): JSX.Element {
  return (
    <div className="onb-brand-header">
      <div className="onb-brand-logo" aria-hidden="true">
        {/* Simbolo CROSS — cata-vento tricolor (mesmo do prototipo) */}
        <svg
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          width="36"
          height="36"
        >
          <path d="M18 18 L18 4 A14 14 0 0 1 29.12 10.88 Z" fill="#3F2570" />
          <path d="M18 18 L29.12 10.88 A14 14 0 0 1 29.12 25.12 Z" fill="#0079DC" />
          <path d="M18 18 L29.12 25.12 A14 14 0 0 1 6.88 25.12 Z" fill="#F9005C" />
          <path d="M18 18 L6.88 25.12 A14 14 0 0 1 6.88 10.88 Z" fill="#3F2570" opacity=".55" />
          <path d="M18 18 L6.88 10.88 A14 14 0 0 1 18 4 Z" fill="#0079DC" opacity=".55" />
          <circle cx="18" cy="18" r="3.5" fill="#FFFFFF" opacity=".92" />
        </svg>
      </div>
      <span className="onb-brand-name" aria-label={t('app.name')}>
        CROSS
      </span>
    </div>
  );
}

// ------------------------------------------------------------------ OnbProgressTrack

interface OnbProgressTrackProps {
  /** Passo atual (1, 2 ou 3) */
  readonly currentStep: 1 | 2 | 3;
}

/**
 * Barra de progresso com labels de passo acima.
 * Reflete exatamente o progress-track do prototipo (33/66/100%).
 */
export function OnbProgressTrack({ currentStep }: OnbProgressTrackProps): JSX.Element {
  const pct = (currentStep / 3) * 100;

  const labels: [string, string, string] = [
    t('onboarding.stepLabel1'),
    t('onboarding.stepLabel2'),
    t('onboarding.stepLabel3'),
  ];

  // Literal lookup seguro (evita template de chave dinamica nao suportado por TranslationKey)
  const eyebrowMap: Record<1 | 2 | 3, string> = {
    1: t('onboarding.stepEyebrow1'),
    2: t('onboarding.stepEyebrow2'),
    3: t('onboarding.stepEyebrow3'),
  };
  const currentEyebrow = eyebrowMap[currentStep];

  return (
    <div
      className="onb-progress-track"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={3}
      aria-valuenow={currentStep}
      aria-label={currentEyebrow}
    >
      <div className="onb-progress-steps" aria-hidden="true">
        {labels.map((label, i) => {
          const stepNum = (i + 1) as 1 | 2 | 3;
          let cls = 'onb-step-label';
          if (stepNum < currentStep) cls += ' onb-step-label-done';
          else if (stepNum === currentStep) cls += ' onb-step-label-active';
          return (
            <span key={stepNum} className={cls}>
              {label}
            </span>
          );
        })}
      </div>
      <div className="onb-progress-bar-wrap">
        <div className="onb-progress-bar-fill" style={{ width: `${pct}%` }} aria-hidden="true" />
      </div>
      <span className="sr-only">{currentEyebrow}</span>
    </div>
  );
}

// ------------------------------------------------------------------ OnbPanelHead

interface OnbPanelHeadProps {
  readonly eyebrow: string;
  /** Centralizar conteudo (passo 3 — sucesso) */
  readonly centered?: boolean;
  readonly children: ReactNode;
}

/**
 * Cabecalho do passo: linha de gradiente (eyebrow) + slot para titulo/subtitulo.
 */
export function OnbPanelHead({
  eyebrow,
  centered = false,
  children,
}: OnbPanelHeadProps): JSX.Element {
  const headCls = centered ? 'onb-panel-head onb-panel-head-center' : 'onb-panel-head';
  const eyebrowCls = centered ? 'onb-panel-eyebrow onb-panel-eyebrow-center' : 'onb-panel-eyebrow';

  return (
    <div className={headCls}>
      <p className={eyebrowCls}>{eyebrow}</p>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ OnbBtnRow

interface OnbBtnRowProps {
  /** Ocultar botao voltar (passo 1) */
  readonly hideBack?: boolean;
  readonly onBack?: () => void;
  readonly backLabel?: string;

  readonly onContinue?: () => void;
  readonly continueLabel?: string;
  readonly continueDisabled?: boolean;
  /** Se true, renderiza um <button type="submit"> em vez de <button type="button"> */
  readonly continueIsSubmit?: boolean;
}

/**
 * Linha de botoes inferior do card: [Voltar] [Continuar].
 * Quando hideBack=true renderiza um placeholder invisivel para manter layout.
 */
export function OnbBtnRow({
  hideBack = false,
  onBack,
  backLabel,
  onContinue,
  continueLabel,
  continueDisabled = false,
  continueIsSubmit = false,
}: OnbBtnRowProps): JSX.Element {
  const backText = backLabel ?? t('onboarding.backButton');
  const continueText = continueLabel ?? t('onboarding.continueToSources');

  return (
    <div className="onb-btn-row">
      {hideBack ? (
        <span className="onb-btn-invisible" aria-hidden="true" />
      ) : (
        <button type="button" className="onb-btn-text" onClick={onBack} aria-label={backText}>
          <span className="material-symbols-rounded onb-btn-icon" aria-hidden="true">
            arrow_back
          </span>
          {backText}
        </button>
      )}

      {continueIsSubmit ? (
        <button
          type="submit"
          className="onb-btn-primary"
          disabled={continueDisabled}
          aria-disabled={continueDisabled}
        >
          {continueText}
          <span className="material-symbols-rounded onb-btn-icon" aria-hidden="true">
            arrow_forward
          </span>
        </button>
      ) : (
        <button
          type="button"
          className="onb-btn-primary"
          onClick={onContinue}
          disabled={continueDisabled}
          aria-disabled={continueDisabled}
        >
          {continueText}
          <span className="material-symbols-rounded onb-btn-icon" aria-hidden="true">
            arrow_forward
          </span>
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ OnbSourceCard

interface OnbSourceCardProps {
  readonly sourceKey: OnbSourceKey;
  readonly connectionKind: OnbConnectionKind;
  readonly connectedLabel?: string | undefined;
  readonly onConnect: () => void;
}

// Dados de apresentacao por fonte — sem strings hardcoded
const SOURCE_META: Record<
  OnbSourceKey,
  {
    nameKey: TranslationKey;
    descKey: TranslationKey;
    /** Material Symbol */
    icon: string;
    /** Classe CSS de cor do icone */
    iconColorClass: string;
    /** Background do wrap em idle (inline style — D-034 PROVISORIO ate brand guidelines) */
    iconBg: string;
  }
> = {
  instagram: {
    nameKey: 'onboarding.sourceInstagram',
    descKey: 'onboarding.sourceInstagramDesc',
    icon: 'photo_camera',
    iconColorClass: 'onb-source-icon-instagram',
    iconBg: '#FFF0F5', // rosa suave — D-034 PROVISORIO
  },
  metaAds: {
    nameKey: 'onboarding.sourceMetaAds',
    descKey: 'onboarding.sourceMetaAdsDesc',
    icon: 'bar_chart',
    iconColorClass: 'onb-source-icon-meta',
    iconBg: '#F0F4FF', // azul suave — D-034 PROVISORIO
  },
  googleDrive: {
    nameKey: 'onboarding.sourceGoogleDrive',
    descKey: 'onboarding.sourceGoogleDriveDesc',
    icon: 'folder_open',
    iconColorClass: 'onb-source-icon-drive',
    iconBg: '#F0ECF8', // lilas suave — D-034 PROVISORIO
  },
};

/**
 * Card de fonte (li) — icone + nome + descricao + botao de acao.
 *
 * FLAG D-034 PROVISORIO: cores de fundo dos icones sao provisorias ate
 * o owner aprovar os brand guidelines definitivos. Quando D-034 for fechada,
 * substituir os inline style por tokens de plataforma e logos SVG oficiais.
 */
export function OnbSourceCard({
  sourceKey,
  connectionKind,
  connectedLabel,
  onConnect,
}: OnbSourceCardProps): JSX.Element {
  const meta = SOURCE_META[sourceKey];
  const name = t(meta.nameKey);
  const desc = connectedLabel ?? t(meta.descKey);
  const statusId = `onb-source-status-${sourceKey}`;

  // Classe do card por estado
  let cardCls = 'onb-source-card';
  if (connectionKind === 'connected') cardCls += ' onb-source-card-connected';
  else if (connectionKind === 'pending') cardCls += ' onb-source-card-pending';
  else if (connectionKind === 'error') cardCls += ' onb-source-card-error';

  // Icone: se conectado, exibe check
  const isConnected = connectionKind === 'connected';
  const iconName = isConnected ? 'check_circle' : meta.icon;
  const iconVariation = isConnected
    ? "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 20"
    : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20";
  const iconColorCls = isConnected ? 'onb-source-icon' : `onb-source-icon ${meta.iconColorClass}`;

  // Botao de conectar: estado e label
  const renderConnectBtn = (): JSX.Element => {
    if (isConnected) {
      return (
        <span
          className="onb-btn-connect onb-btn-connect-connected"
          aria-label={t('onboarding.sourceStatusConnected')}
        >
          <span
            className="material-symbols-rounded onb-btn-icon"
            aria-hidden="true"
            style={{
              fontVariationSettings: "'FILL' 1,'wght' 500,'GRAD' 0,'opsz' 20",
              color: 'var(--sem-success)',
              fontSize: '18px',
            }}
          >
            check_circle
          </span>
        </span>
      );
    }

    if (connectionKind === 'connecting') {
      return (
        <button
          type="button"
          className="onb-btn-connect"
          disabled
          aria-label={t('onboarding.sourceStatusConnecting')}
          aria-describedby={statusId}
        >
          <span className="onb-spinner" aria-hidden="true" />
          {t('onboarding.sourceStatusConnecting')}
        </button>
      );
    }

    if (connectionKind === 'pending') {
      return (
        <button
          type="button"
          className="onb-btn-connect onb-btn-connect-pending"
          disabled
          aria-label={t('onboarding.sourceStatusPending')}
          aria-describedby={statusId}
        >
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{
              fontSize: '14px',
              fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20",
            }}
          >
            schedule
          </span>
          {t('onboarding.sourceStatusPending')}
        </button>
      );
    }

    const isError = connectionKind === 'error';
    const label = isError ? t('onboarding.sourceRetryCta') : t('onboarding.sourceConnectCta');
    const btnCls = isError ? 'onb-btn-connect onb-btn-connect-error' : 'onb-btn-connect';

    return (
      <button
        type="button"
        className={btnCls}
        onClick={onConnect}
        aria-label={`${label} ${name}`}
        aria-describedby={statusId}
      >
        <span
          className="material-symbols-rounded"
          aria-hidden="true"
          style={{
            fontSize: '14px',
            fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20",
          }}
        >
          {isError ? 'refresh' : 'link'}
        </span>
        {label}
      </button>
    );
  };

  return (
    <li className={cardCls} aria-describedby={statusId}>
      {/* Icone da fonte */}
      {/* FLAG D-034 PROVISORIO: iconBg inline ate brand guidelines aprovados */}
      <div
        className="onb-source-icon-wrap"
        aria-hidden="true"
        style={isConnected ? undefined : { background: meta.iconBg }}
      >
        <span
          className={`material-symbols-rounded ${iconColorCls}`}
          style={{ fontVariationSettings: iconVariation }}
        >
          {iconName}
        </span>
      </div>

      {/* Nome e descricao */}
      <div className="onb-source-info">
        <p className="onb-source-name">{name}</p>
        <p className="onb-source-desc" id={statusId}>
          {desc}
        </p>
        {/* Status acessivel para leitores de tela */}
        {connectionKind !== 'idle' && connectionKind !== 'connected' ? (
          <span role="status" className="sr-only">
            {connectionKind === 'connecting'
              ? t('onboarding.sourceStatusConnecting')
              : connectionKind === 'pending'
                ? t('onboarding.sourceStatusPending')
                : t('onboarding.sourceStatusError')}
          </span>
        ) : null}
      </div>

      {/* Botao de acao */}
      <div style={{ flexShrink: 0 }}>{renderConnectBtn()}</div>
    </li>
  );
}

// ------------------------------------------------------------------ OnbTimelineItem

interface OnbTimelineItemProps {
  readonly icon: string;
  readonly done: boolean;
  readonly label: string;
  readonly meta: string;
}

/**
 * Item da timeline de sucesso (passo 3).
 * done=true -> ponto verde check; done=false -> ponto roxo pendente.
 */
export function OnbTimelineItem({ icon, done, label, meta }: OnbTimelineItemProps): JSX.Element {
  const dotCls = done
    ? 'onb-timeline-dot onb-timeline-dot-done'
    : 'onb-timeline-dot onb-timeline-dot-pending';

  const iconVariation = done
    ? "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 20"
    : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20";

  return (
    <li className="onb-timeline-item">
      <div className={dotCls} aria-hidden="true">
        <span
          className={`material-symbols-rounded onb-timeline-dot-icon`}
          style={{ fontVariationSettings: iconVariation }}
        >
          {icon}
        </span>
      </div>
      <div className="onb-timeline-text">
        <p className="onb-timeline-label">{label}</p>
        <p className="onb-timeline-meta">{meta}</p>
      </div>
    </li>
  );
}
